import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Container from '../container';
import Chevron from '../../images/svg/chevron-icon.svg';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import useEmblaCarousel from 'embla-carousel-react';
import ProductSliderProductCard from './ProductSliderProductCard';
import Button from '../button';
import parseStoryblokLink from '../../utils/parseStoryblokLink';
import mq from '../../utils/mq';

const ProductSliderOuterContainer = styled.div<{
  $includePadding?: boolean;
  $theme?: 'white' | 'grey';
}>`
  width: 100%;
  height: 100%;
  padding: ${({ $includePadding }) => ($includePadding ? '96px 0' : '0')};
  background: ${({ $theme }) =>
    $theme === 'white' ? '#fff' : 'var(--Color-Grey-05, #F8F8F8)'};
  ${mq.mobile(css<{ $includePadding?: boolean }>`
    padding: ${({ $includePadding }) => ($includePadding ? '48px 0' : '0')};
  `)};
`;

const ProductSliderTitleSection = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 38px;
  ${mq.mobile(css`
    gap: 20px;
    flex-wrap: wrap;
    padding-bottom: 20px;
  `)};
`;

const ProductSliderTitle = styled.h2`
  color: #161616;
  font-size: 27px;
  font-style: normal;
  font-weight: 6700;
  line-height: 120%;
  ${mq.mobile(css`
    font-size: 27px;
  `)};
`;

const ProductSliderControls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

const ProductSliderControlButton = styled.button<{
  disabled?: boolean;
  $theme?: 'white' | 'grey';
}>`
  width: 48px;
  height: 48px;
  background: ${({ $theme }) => ($theme === 'white' ? '#F5F5F5' : '#fff')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #161616;
  border: none;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      svg {
        opacity: 0.3;
      }
    `}
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProductSliderProductsContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: visible;
  overscroll-behavior-x: contain;
`;

const EmblaContainer = styled.div`
  display: flex;
  overflow-x: visible;
`;

const CtaContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 56px;
  ${mq.mobile(css`
    margin-top: 32px;
  `)};
`;

export default function ProductSlider({
  title,
  products,
  ctaLink,
  ctaText,
  includePadding = false,
  theme
}: {
  title: string;
  products: any[];
  ctaLink?: string;
  ctaText?: string;
  includePadding?: boolean;
  theme?: 'white' | 'grey';
}) {
  const [canScrollNext, setcanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      skipSnaps: true,
      dragThreshold: 0.2,
      align: 'start'
    },
    [
      WheelGesturesPlugin({
        forceWheelAxis: 'x'
      })
    ]
  );

  const previousSlide = () => {
    if (emblaApi) {
      if (emblaApi.canScrollPrev()) {
        emblaApi.scrollPrev();
      }
    }
  };

  const nextSlide = () => {
    if (emblaApi) {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      }
    }
  };

  emblaApi?.on('scroll', () => {
    setcanScrollNext(emblaApi?.canScrollNext() || false);
    setCanScrollPrev(emblaApi?.canScrollPrev() || false);
  });

  emblaApi?.on('init', () => {
    setcanScrollNext(emblaApi?.canScrollNext() || false);
    setCanScrollPrev(emblaApi?.canScrollPrev() || false);
  });
  return (
    <ProductSliderOuterContainer
      $includePadding={includePadding}
      $theme={theme}
    >
      <Container style={{ overflowX: 'visible' }}>
        <ProductSliderTitleSection>
          <ProductSliderTitle>{title}</ProductSliderTitle>
          <ProductSliderControls>
            <ProductSliderControlButton
              onClick={previousSlide}
              disabled={!canScrollPrev}
              $theme={theme}
            >
              <Chevron />
            </ProductSliderControlButton>
            <ProductSliderControlButton
              onClick={nextSlide}
              disabled={!canScrollNext}
              $theme={theme}
            >
              <Chevron style={{ transform: 'rotate(180deg)' }} />
            </ProductSliderControlButton>
          </ProductSliderControls>
        </ProductSliderTitleSection>
        <ProductSliderProductsContainer ref={emblaRef as any} className="embla">
          <EmblaContainer className="embla__container">
            {products?.map((product, index) => (
              <ProductSliderProductCard
                key={index}
                product={product}
                theme={theme}
              />
            ))}
          </EmblaContainer>
        </ProductSliderProductsContainer>
        {ctaLink && ctaText && (
          <CtaContainer>
            <Button to={parseStoryblokLink(ctaLink as any)} borderGrey>
              {ctaText}
            </Button>
          </CtaContainer>
        )}
      </Container>
    </ProductSliderOuterContainer>
  );
}
